"use client";

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useRef,
  useCallback,
  type JSX,
} from "react";
import { z } from 'zod/v3';
import { Database } from "@/types/supabase";
import {
  buildTreeFromTreeView,
} from "@/app/components/locations/treeHelpers"; // Import the tree builder
import { LocationProcessingStatus } from "@/app/components/locations/LocationStatusItem";
import { deleteItems } from "@/app/actions/locationWorkflow";
import {
  createTreeFolder,
  renameTreeNode,
  deleteTreeNode as deleteTreeNodeAction,
} from "@/app/actions/tree";
import { useSupabase } from "@/lib/supabase/SupabaseProvider";
import { useSubscription } from "@/hooks/useSubscription";
import { TreeViewRow } from "@/types/treeView";
import { TeamTreeRow } from "@/types/teamTree";

// Type aliases for Supabase rows
export type FileUploadRow = Database["public"]["Tables"]["file_uploads"]["Row"];
type DocumentGroupRow = Database["public"]["Tables"]["document_groups"]["Row"];
export type LocationRow = Database["public"]["Tables"]["locations"]["Row"];
type DocumentStagingRow =
  Database["public"]["Tables"]["document_staging"]["Row"];

// Data structure to hold references to the original database records
export interface SourceData {
  documentGroup?: DocumentGroupRow;
  fileUpload?: FileUploadRow;
  documentStaging?: DocumentStagingRow;
  dbLocation?: LocationRow; // Represents a record from the 'locations' table
}

// Represents a node in the unified tree
export interface UnifiedTreeNode {
  id: string; // tree_nodes uuid ("file:<file_upload_id>" for files) — stable across rename/move
  parentNodeId: string | null; // parent tree node id
  name: string; // Display name of the node
  pathSegments: string[]; // Derived path as an array of strings (display / dnd only)
  fullPath: string; // Derived full path string
  nodeType: "UI_LOCATION_FOLDER" | "FILE_ITEM";
  isShared?: boolean; // visible via a team share rather than ownership
  sourceData: SourceData;

  // UI specific state
  isExpanded?: boolean;
  isSelected?: boolean;
  // Add other UI states as needed, like isClickable, validated from old LocationState

  // Children
  children: UnifiedTreeNode[];

  // Aggregated data (if this node is a folder type)
  docCount: number;
  directFileCount: number;
  aggregatedFileStatus:
  | LocationProcessingStatus
  | "preparing"
  | "in_progress"
  | "completed"
  | "error";
  aggregatedFileProgress: number;
  debugSourceInfo?: string[]; // For debugging tree construction

  // Fields from old LocationState that might still be relevant at the node level
  created_at?: string;
  updated_at?: string;
  validated?: boolean | null; // From dbLocation or relevant source
  isClickable?: boolean; // Determine based on nodeType and content

  // Contract AST ingestion status (two-stage completion tracking)
  contractAstStatus?: 'success' | 'processing' | 'error' | 'waiting' | 'skipped' | null;

  // Temporary for transition, try to source these from sourceData or aggregate them
  // status?: "ready" | "in_progress" | "preparing"; // This should be replaced by aggregatedFileStatus for folders
  // documents?: any[]; // This should be replaced by children of FILE_ITEM type or aggregated from sourceData.fileUpload/documentStaging
}


interface ReviewContextType {
  // locations: LocationState[]; // To be replaced by the tree root nodes
  treeData: UnifiedTreeNode[]; // The root nodes of the new unified tree
  nodeMap: Map<string, UnifiedTreeNode>; // Flat map for direct node access by ID (path string)
  selectedNodeId: string | null; // ID of the selected node in the tree
  setSelectedNodeId: (nodeId: string | null) => void;
  // setSelectedLocation: (location: LocationData | null) => void; // Replace with setSelectedNodeId
  // selectedLocation: LocationData | null; // To be derived from selectedNodeId and nodeMap if needed for detail views

  validationErrors?: z.ZodError;
  setValidationErrors: (errors?: z.ZodError) => void;
  editField: (nodeId: string, fieldPath: string) => void; // fieldPath relative to node's data
  editingField: { nodeId: string; fieldPath: string } | null;
  // updateLocation: (location: LocationData, fieldPath: string) => Promise<void>; // To be adapted for UnifiedTreeNode
  updateNodeData: (
    nodeId: string,
    updatedData: Partial<UnifiedTreeNode> | Partial<SourceData>,
    fieldPath?: string
  ) => Promise<void>;

  viewedLocations: Set<string>; // Potentially IDs of nodes that have been "viewed"
  setViewedLocations: (locations: Set<string>) => void;
  inProgressLocations: Set<string>; // Potentially IDs of nodes that are "in_progress"
  setLocationInProgress: (locationId: string) => void; // locationId would be a node ID

  deleteNode: (nodeToDelete: UnifiedTreeNode) => Promise<void>; // Replaces deleteLocation
  // deleteLocation: (
  //   locationId: string,
  //   locationGroupPath: string
  // ) => Promise<void>;

  // documentStates: DocumentStagingState[]; // This data will be integrated into UnifiedTreeNode.sourceData
  // setDocumentStates: (states: DocumentStagingState[]) => void;

  allUploadsComplete: boolean; // This will be determined by checking all relevant nodes in the tree/map
  setAllUploadsComplete: (complete: boolean) => void;
  fetchAndMergeData: (forceRefresh?: boolean) => Promise<void>; // Renamed from fetchAndMergeLocations

  // Folders are real tree_nodes rows; returns the new node id.
  createFolder: (name: string, parentId?: string | null) => Promise<{ id?: string; error?: string }>;

  // Team-owned locations (rendered in the Teams section, not the personal tree)
  teamLocations: TeamTreeRow[];
  // Raw tree_view rows (own + shared) — the Teams section rebuilds each
  // team's folder structure from the ancestor chains in here.
  treeViewRows: TreeViewRow[];

  // Method to optimistically update a location's validated status
  updateLocationValidatedStatus: (
    locationId: string,
    validated: boolean
  ) => void;

  // Raw file uploads data from database (for upload manager to watch)
  rawFileUploads: FileUploadRow[];
}

const ReviewContext = createContext<ReviewContextType | undefined>(undefined);

export const ReviewProvider = ({ children }: { children: ReactNode }): JSX.Element => {
  // Raw data from Supabase - the tree_view over tree_nodes
  const [treeViewRows, setTreeViewRows] = useState<TreeViewRow[]>([]);
  const [teamLocations, setTeamLocations] = useState<TeamTreeRow[]>([]);

  // Keep the raw data states for backward compatibility during transition
  const [rawDocumentGroups, setRawDocumentGroups] = useState<
    DocumentGroupRow[]
  >([]);
  const [rawFileUploads, setRawFileUploads] = useState<FileUploadRow[]>([]);
  const [rawStagingData, setRawStagingData] = useState<DocumentStagingRow[]>(
    []
  );
  const [rawDbLocations, setRawDbLocations] = useState<LocationRow[]>([]);

  // State for the unified tree and node map
  const [treeData, setTreeData] = useState<UnifiedTreeNode[]>([]);
  const [nodeMap, setNodeMap] = useState<Map<string, UnifiedTreeNode>>(new Map());

  // Function to recursively build a map of node IDs to nodes
  const buildNodeMap = useCallback((nodes: UnifiedTreeNode[]): Map<string, UnifiedTreeNode> => {
    const map = new Map<string, UnifiedTreeNode>();

    function processNode(node: UnifiedTreeNode) {
      map.set(node.id, node);
      if (node.children && node.children.length > 0) {
        node.children.forEach(processNode);
      }
    }

    nodes.forEach(processNode);
    return map;
  }, []);

  // Keep track of previous log values to avoid duplicate logging
  const prevNodeMapStats = useRef<{ size: number; clickable: number }>({ size: 0, clickable: 0 });

  // Keep nodeMap in sync with treeData and update isClickable
  useEffect(() => {
    const newMap = buildNodeMap(treeData);

    // Update isClickable property based on dbLocation presence
    let clickableCount = 0;
    newMap.forEach((node) => {
      node.isClickable = !!node.sourceData.dbLocation?.id && node.sourceData.dbLocation?.isProcessed === true;
      if (node.isClickable) clickableCount++;
      // Also update validated from dbLocation
      if (node.sourceData.dbLocation) {
        node.validated = node.sourceData.dbLocation.validated;
      }
    });

    // Only log if values have changed
    if (prevNodeMapStats.current.size !== newMap.size || prevNodeMapStats.current.clickable !== clickableCount) {
      prevNodeMapStats.current = { size: newMap.size, clickable: clickableCount };
    }

    setNodeMap(newMap);
  }, [treeData, buildNodeMap]);

  // State for uploads and selections
  const [allUploadsComplete, setAllUploadsComplete] = useState(false);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);

  const [validationErrors, setValidationErrors] = useState<
    z.ZodError | undefined
  >();
  // const [editingField, setEditingField] = useState<string | null>(null); // Old state
  const [editingField, setEditingField] = useState<{
    nodeId: string;
    fieldPath: string;
  } | null>(null); // New state

  const [viewedLocations, setViewedLocations] = useState<Set<string>>(
    new Set()
  );
  const [inProgressLocations, setInProgressLocations] = useState<Set<string>>(
    new Set()
  );

  const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const nodeMapRef = useRef(nodeMap);
  nodeMapRef.current = nodeMap;

  const { supabase } = useSupabase();

  const clearRetryTimeout = useCallback(() => {
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }
  }, []);

  // Fetch and build tree from tree_view
  const fetchAndMergeData = useCallback(
    async (forceRefresh?: boolean, attempt = 1) => {
      clearRetryTimeout();
      try {
        // tree_view / team_tree_view are not in the generated Database types yet
        const [treeRes, teamRes] = await Promise.all([
          (supabase as any).from("tree_view").select("*"),
          (supabase as any).from("team_tree_view").select("*"),
        ]);

        if (treeRes.error) throw treeRes.error;
        setTreeViewRows((treeRes.data as TreeViewRow[]) || []);
        if (teamRes.error) {
          console.error("[ReviewContext] Failed to fetch team_tree_view:", teamRes.error.message ?? teamRes.error);
        } else {
          setTeamLocations((teamRes.data as TeamTreeRow[]) || []);
        }
      } catch (error) {
        // Log the message explicitly: the browser console (and Next's dev
        // overlay especially) often flattens Error/PostgrestError objects to
        // "{}" in its display, hiding the one field that explains the failure.
        console.error(
          "[ReviewContext] Failed to fetch tree_view:",
          error instanceof Error ? error.message : error
        );
        if (attempt < 3) {
          retryTimeoutRef.current = setTimeout(
            () => fetchAndMergeData(forceRefresh, attempt + 1),
            1000 * attempt
          );
        }
      }
    },
    [supabase, clearRetryTimeout]
  );

  const prevNodeStatusKey = useRef<string>('');

  // This effect will run whenever the data changes, rebuilding the tree.
  useEffect(() => {
    let newTree: UnifiedTreeNode[] = [];
    let newMap: Map<string, UnifiedTreeNode> = new Map();

    // Team-owned locations render in the Teams section, not the personal
    // tree — drop the shared-graft rows tree_view still emits.
    const result = buildTreeFromTreeView(treeViewRows.filter((r) => !r.is_shared));
    newTree = result.tree;
    newMap = result.map;

    // Update isClickable and validated properties
    let clickableNodes = [];
    let preparingNodes = [];
    newMap.forEach((node) => {
      // A node is clickable only if it has a processed location
      node.isClickable = !!node.sourceData.dbLocation?.id && node.sourceData.dbLocation?.isProcessed === true;

      if (node.sourceData.dbLocation) {
        node.validated = node.sourceData.dbLocation.validated;

        if (node.isClickable) {
          clickableNodes.push({
            id: node.id,
            name: node.name,
            locationId: node.sourceData.dbLocation.id
          });
        } else if (node.sourceData.dbLocation.isProcessed === false) {
          preparingNodes.push({
            id: node.id,
            name: node.name,
            locationId: node.sourceData.dbLocation.id
          });
        }
      }
    });

    // Only log node status if there's a meaningful change
    const nodeStatusKey = `${clickableNodes.length}-${preparingNodes.length}-${newMap.size}`;
    if (prevNodeStatusKey.current !== nodeStatusKey && (clickableNodes.length > 0 || preparingNodes.length > 0)) {
      prevNodeStatusKey.current = nodeStatusKey;
    }

    // Also update isClickable in the tree structure
    const updateTreeNodesRecursively = (nodes: UnifiedTreeNode[]) => {
      nodes.forEach(node => {
        node.isClickable = !!node.sourceData.dbLocation?.id && node.sourceData.dbLocation?.isProcessed === true;
        if (node.sourceData.dbLocation) {
          node.validated = node.sourceData.dbLocation.validated;
        }
        if (node.children && node.children.length > 0) {
          updateTreeNodesRecursively(node.children);
        }
      });
    };

    updateTreeNodesRecursively(newTree);

    setTreeData(newTree);
    setNodeMap(newMap);

    // Optional: Recalculate allUploadsComplete if needed, similar logic as before
    // This could also be a useMemo that depends on the newMap.
  }, [treeViewRows]);

  // Create a ref to store the latest fetchAndMergeData to avoid recreating subscriptions
  const fetchAndMergeDataRef = useRef(fetchAndMergeData);
  fetchAndMergeDataRef.current = fetchAndMergeData;

  useEffect(() => {
    fetchAndMergeData(); // Initial fetch
  }, [fetchAndMergeData]);

  // Recursive helper to update a node within the tree structure.
  const updateNodeInTree = (
    nodes: UnifiedTreeNode[],
    nodeId: string,
    updateFn: (node: UnifiedTreeNode) => UnifiedTreeNode
  ): UnifiedTreeNode[] => {
    return nodes.map((node) => {
      if (node.id === nodeId) {
        return updateFn(node);
      }
      if (node.children && node.children.length > 0) {
        const newChildren = updateNodeInTree(node.children, nodeId, updateFn);
        if (newChildren !== node.children) {
          // If children have changed, return new parent with updated children.
          // Aggregates will refresh on next node_view fetch.
          return { ...node, children: newChildren };
        }
      }
      return node;
    });
  };

  // --- Subscription Logic ---
  // Debounced refetch keeps the tree live (processing progress, pipeline
  // inserts, tree changes from other tabs) without a query per event.
  const treeRefetchTimerRef = useRef<NodeJS.Timeout | null>(null);
  const scheduleTreeRefetch = useCallback(() => {
    if (treeRefetchTimerRef.current) clearTimeout(treeRefetchTimerRef.current);
    treeRefetchTimerRef.current = setTimeout(() => {
      treeRefetchTimerRef.current = null;
      fetchAndMergeDataRef.current(true);
    }, 750);
  }, []);

  const handleDatabaseChange = useCallback((payload: any) => {
    const { table, eventType, new: newRecord, old: oldRecord } = payload;

    const relevantTables = [
      "document_staging",
      "locations",
      "document_groups",
      "file_uploads",
      "tree_nodes",
      "team_locations",
      "teams",
    ];
    if (!relevantTables.includes(table)) return;

    scheduleTreeRefetch();
    if (table === "tree_nodes" || table === "team_locations" || table === "teams") {
      return; // no raw-array bookkeeping for tree/team rows
    }

    switch (eventType) {
      case "INSERT":
        switch (table) {
          case "document_groups":
            setRawDocumentGroups((prev) => [...prev, newRecord]);
            break;
          case "file_uploads":
            setRawFileUploads((prev) => [...prev, newRecord]);
            break;
          case "document_staging":
            setRawStagingData((prev) => [...prev, newRecord]);
            break;
          case "locations":
            setRawDbLocations((prev) => [...prev, newRecord]);
            break;
        }
        break;
      case "UPDATE":
        switch (table) {
          case "document_groups":
            setRawDocumentGroups((prev) =>
              prev.map((r) => (r.id === newRecord.id ? newRecord : r))
            );
            break;
          case "file_uploads":
            setRawFileUploads((prev) =>
              prev.map((r) => (r.id === newRecord.id ? newRecord : r))
            );
            break;
          case "document_staging":
            setRawStagingData((prev) =>
              prev.map((r) => (r.id === newRecord.id ? newRecord : r))
            );
            break;
          case "locations":
            // Log all location updates with key changes highlighted
            const changes = [];
            if (oldRecord?.isProcessed !== newRecord.isProcessed) {
              changes.push(`isProcessed: ${oldRecord?.isProcessed} → ${newRecord.isProcessed}`);
            }
            if (oldRecord?.status !== newRecord.status) {
              changes.push(`status: ${oldRecord?.status} → ${newRecord.status}`);
            }
            if (oldRecord?.validated !== newRecord.validated) {
              changes.push(`validated: ${oldRecord?.validated} → ${newRecord.validated}`);
            }

            // Important: When isProcessed changes to true, the location becomes clickable
            if (oldRecord?.isProcessed === false && newRecord.isProcessed === true) {
              console.log(`[ReviewContext] Location ${newRecord.group} is now CLICKABLE`);
            }

            setRawDbLocations((prev) =>
              prev.map((r) => (r.id === newRecord.id ? newRecord : r))
            );
            break;
        }
        break;
      case "DELETE":
        const idToDelete = oldRecord.id;
        if (!idToDelete) break;
        switch (table) {
          case "document_groups":
            setRawDocumentGroups((prev) =>
              prev.filter((r) => r.id !== idToDelete)
            );
            break;
          case "file_uploads":
            setRawFileUploads((prev) =>
              prev.filter((r) => r.id !== idToDelete)
            );
            break;
          case "document_staging":
            setRawStagingData((prev) =>
              prev.filter((r) => r.id !== idToDelete)
            );
            break;
          case "locations":
            setRawDbLocations((prev) =>
              prev.filter((r) => r.id !== idToDelete)
            );
            break;
        }
        break;
      default:
        console.log(
          `[ReviewContext] Unhandled event type "${eventType}". Triggering full refetch.`
        );
        setTimeout(() => fetchAndMergeDataRef.current(true), 100);
        break;
    }
  }, [scheduleTreeRefetch]);

  // Setup a single subscription for all table changes in the public schema.
  useSubscription("*", handleDatabaseChange);

  const editField = useCallback((nodeId: string, fieldPath: string) => {
    setEditingField({ nodeId, fieldPath });
  }, []);

  const updateNodeData = useCallback(
    async (
      nodeId: string, // The path-based ID of the node in the tree
      changes: Partial<UnifiedTreeNode> | Partial<SourceData>, // Data to update (node properties or sourceData)
      fieldPath?: string // Optional: Describes the specific field path (e.g., "address", "landlord.name", "name")
    ) => {

      // 1. Retrieve the node to be updated.
      const nodeToUpdate = nodeMapRef.current.get(nodeId);
      if (!nodeToUpdate) {
        console.error(
          `[ReviewContext] updateNodeData: Node with ID '${nodeId}' not found. Update cannot proceed.`
        );
        return;
      }

      // 2. Determine if this is a rename operation: the node id IS the
      // tree_nodes uuid, so a rename is a single-row update server-side.
      const isFolderRename =
        "name" in changes &&
        typeof changes.name === 'string' &&
        changes.name !== nodeToUpdate.name &&
        nodeToUpdate.nodeType === "UI_LOCATION_FOLDER";

      // 3. --- Backend Persistence Logic ---
      if (isFolderRename) {
        const newName = (changes as { name: string }).name;

        // Optimistic: node ids are stable, so just patch the name in place.
        setNodeMap((prevMap) => {
          const newMap = new Map(prevMap);
          const current = newMap.get(nodeId);
          if (current) newMap.set(nodeId, { ...current, name: newName });
          return newMap;
        });
        setTreeData((prevTree) =>
          updateNodeInTree(prevTree, nodeId, (n) => ({ ...n, name: newName }))
        );

        const result = await renameTreeNode(nodeId, newName);
        await fetchAndMergeDataRef.current(true);
        if (result.error) {
          throw new Error(result.error);
        }
      } else {
        // 3b. Handle Field Updates (logic from rl-location-profile-updates branch)
        // Field update for node
        try {
          let serverUpdateError: any = null;
          if ((changes as Partial<SourceData>).dbLocation) {
            const dbLocationChanges = (changes as Partial<SourceData>)
              .dbLocation as LocationRow;
            const locationRecordId = nodeToUpdate.sourceData.dbLocation?.id;

            if (locationRecordId && Object.keys(dbLocationChanges).length > 0) {
              const { error } = await supabase
                .from("locations")
                .update(dbLocationChanges)
                .eq("id", locationRecordId);

              if (error) {
                serverUpdateError = error;
              } else {
                // Update successful
              }
            } else if (!locationRecordId) {
              console.warn(
                `[ReviewContext] updateNodeData: 'dbLocation.id' not found for node '${nodeId}'. Cannot persist.`
              );
            }
          }
          // TODO: Add similar blocks for other sourceData types if they can be edited this way

          if (serverUpdateError) {
            console.warn("[ReviewContext] updateNodeData: Server update for field failed. Optimistic update will still apply.", serverUpdateError);
          }
        } catch (e) {
          console.error("[ReviewContext] updateNodeData: Exception during field update persistence:", e);
        }
      }

      // 4. --- Optimistic Client-Side Update (Unified logic from rl-location-profile-updates) ---
      // This will apply the 'changes' to the local state.
      // For folder renames, 'changes' would be like { name: 'newFolderName' }.
      // For field updates, 'changes' would be like { dbLocation: { address: 'newAddress' } }.
      // This section should run *after* the backend attempt for field updates,
      // but for renames, the refresh (`fetchAndMergeDataRef.current(true)`) handles data consistency
      // so this optimistic update mainly provides immediate UI feedback if not for the refresh.
      // If a refresh happens after rename, this optimistic update might be momentarily visible.
      if (!isFolderRename) { // Only run this detailed optimistic update if not a folder rename that will hard refresh
        setNodeMap((prevMap) => {
          const newMap = new Map(prevMap);
          const currentNode = newMap.get(nodeId);

          if (currentNode) {
            let updatedNodeState = { ...currentNode };
            const sourceDataKeys: Array<keyof SourceData> = [
              "documentGroup", "fileUpload", "documentStaging", "dbLocation",
            ];
            const treeNodeSpecificChanges: Partial<UnifiedTreeNode> = {};
            const sourceDataSpecificChanges: Partial<SourceData> = {};

            for (const key in changes) {
              if (Object.prototype.hasOwnProperty.call(changes, key)) {
                if (sourceDataKeys.includes(key as keyof SourceData)) {
                  (sourceDataSpecificChanges as any)[key] = (changes as any)[key];
                } else {
                  (treeNodeSpecificChanges as any)[key] = (changes as any)[key];
                }
              }
            }

            updatedNodeState = { ...updatedNodeState, ...treeNodeSpecificChanges };
            let newSourceData = { ...updatedNodeState.sourceData };

            if (sourceDataSpecificChanges.dbLocation) {
              newSourceData.dbLocation = {
                ...sourceDataSpecificChanges.dbLocation,
                updated_at: new Date().toISOString(),
              } as LocationRow;
            }
            if (sourceDataSpecificChanges.documentGroup) {
              newSourceData.documentGroup = {
                ...(newSourceData.documentGroup || {}),
                ...sourceDataSpecificChanges.documentGroup,
                updated_at: new Date().toISOString(),
              } as DocumentGroupRow;
            }
            updatedNodeState.sourceData = newSourceData;
            newMap.set(nodeId, updatedNodeState);
            setTreeData((prevTree) =>
              updateNodeInTree(prevTree, nodeId, () => updatedNodeState)
            );
          }
          return newMap;
        });
      }

      // 5. Clear the global editing state.
      setEditingField(null);
    },
    // Combine dependencies from both branches, ensuring stability
    [supabase, updateNodeInTree, fetchAndMergeDataRef]
  );

  const createFolder = useCallback(
    async (name: string, parentId: string | null = null) => {
      const result = await createTreeFolder(parentId, name);
      if (!result.error) {
        await fetchAndMergeDataRef.current(true);
      }
      return result;
    },
    []
  );

  const deleteNode = useCallback(
    async (nodeToDelete: UnifiedTreeNode) => {
      console.log(`[ReviewContext] Deleting: ${nodeToDelete.name}`);

      if (nodeToDelete.nodeType === "FILE_ITEM") {
        // Single file: delete the file_upload row; staging cascades, S3/ast
        // cleanup is handled inside deleteItems' file_uploads branch.
        const fileUploadId = nodeToDelete.sourceData.fileUpload?.id;
        if (!fileUploadId) return;
        const result = await deleteItems([
          { id: fileUploadId, tableName: "file_uploads" },
        ]);
        if (!result.success) {
          throw new Error(result.message);
        }
        await fetchAndMergeDataRef.current(true);
        return;
      }

      // Container: server walks the subtree (S3 + contract_ast cleanup, then
      // node delete with FK cascades).
      const result = await deleteTreeNodeAction(nodeToDelete.id);
      await fetchAndMergeDataRef.current(true);
      if (result.error) {
        throw new Error(result.error);
      }
    },
    []
  );

  const setLocationInProgress = useCallback((nodeId: string) => {
    // nodeId instead of locationId
    setInProgressLocations((prev) => new Set([...prev, nodeId]));
    // Optionally update the node in nodeMap directly for immediate UI feedback
    const node = nodeMapRef.current.get(nodeId);
    if (node) {
      // You might change a property on the node like 'aggregatedFileStatus'
      // This requires careful state management to ensure React picks up the change.
    }
  }, []);

  // Method to optimistically update a location's validated status
  const updateLocationValidatedStatus = useCallback(
    (locationId: string, validated: boolean) => {
      // Log validation status changes only

      setNodeMap((prevMap) => {
        const newMap = new Map(prevMap);
        let updated = false;

        // Find and update the node with matching dbLocation.id
        for (const [nodeId, node] of newMap.entries()) {
          if (node.sourceData.dbLocation?.id === locationId) {
            const updatedNode = {
              ...node,
              sourceData: {
                ...node.sourceData,
                dbLocation: {
                  ...node.sourceData.dbLocation,
                  validated,
                  updated_at: new Date().toISOString(),
                },
              },
            };
            newMap.set(nodeId, updatedNode);
            updated = true;
            console.log(
              `[ReviewContext] Location ${locationId} validated: ${validated}`
            );
            break;
          }
        }

        if (!updated) {
          console.warn(
            `[ReviewContext] Could not find node for location ${locationId} to update validated status`
          );
        }

        return newMap;
      });

      // Update treeData to reflect the change (simplified update)
      setTreeData((prevTree) => {
        return prevTree.map((rootNode) => {
          if (rootNode.sourceData.dbLocation?.id === locationId) {
            return {
              ...rootNode,
              sourceData: {
                ...rootNode.sourceData,
                dbLocation: {
                  ...rootNode.sourceData.dbLocation,
                  validated,
                  updated_at: new Date().toISOString(),
                },
              },
            };
          }
          // For nested nodes, would need recursive update
          return rootNode;
        });
      });
    },
    []
  );

  const contextValue: ReviewContextType = {
    treeData,
    nodeMap,
    selectedNodeId,
    setSelectedNodeId,
    validationErrors,
    setValidationErrors,
    editField,
    editingField,
    updateNodeData,
    viewedLocations,
    setViewedLocations,
    inProgressLocations,
    setLocationInProgress,
    deleteNode,
    allUploadsComplete,
    setAllUploadsComplete,
    fetchAndMergeData,
    createFolder,
    teamLocations,
    treeViewRows,
    updateLocationValidatedStatus,
    rawFileUploads,
  };

  // useMemo for contextValue is important
  const memoizedContextValue = React.useMemo(
    () => contextValue,
    [
      treeData,
      nodeMap,
      selectedNodeId,
      validationErrors,
      editingField,
      viewedLocations,
      inProgressLocations,
      allUploadsComplete,
      updateLocationValidatedStatus,
      deleteNode,
      createFolder,
      teamLocations,
      treeViewRows,
      setLocationInProgress,
      editField,
      updateNodeData,
      rawFileUploads,
    ]
  );

  useEffect(() => {
    // console.log('[ReviewContext] State update - Tree Nodes:', treeData.length, 'NodeMap size:', nodeMap.size, 'Selected Node ID:', selectedNodeId);
  }, [treeData, nodeMap, selectedNodeId]);

  return (
    <ReviewContext.Provider value={memoizedContextValue}>
      {children}
    </ReviewContext.Provider>
  );
};

// Export the hook as a named export
export function useReview(): ReviewContextType {
  const context = useContext(ReviewContext);
  if (context === undefined) {
    throw new Error("useReview must be used within a ReviewProvider");
  }
  return context;
}

// Also export the context itself as a named export
export { ReviewContext };

// And as a default export for backward compatibility
export default ReviewContext;
